import { Controller } from "@hotwired/stimulus"

const DAY_SECONDS = 24 * 60 * 60

// Times are handled as seconds since the start of the selected day, in the
// server's time zone: the browser's own zone never enters the math, and the
// playback endpoint gets plain "YYYY-MM-DD HH:MM:SS" strings it parses in
// that same zone.
export default class extends Controller {
  static values = {
    date: String,
    defaultSeconds: Number,
    maxSeconds: Number,
    noRecordingText: String,
    hintText: String,
    tooLongText: String
  }
  static targets = ["lane", "block", "mark", "form", "channel", "from", "to", "action", "hint"]

  connect() {
    this.edited()
  }

  markTargetConnected() {
    this.edited()
  }

  changeDay(event) {
    event.target.form.requestSubmit()
  }

  pick(event) {
    const lane = event.currentTarget
    const channel = lane.dataset.channel
    const rect = lane.getBoundingClientRect()
    const at = Math.floor(((event.clientX - rect.left) / rect.width) * DAY_SECONDS)
    const block = this.#blockAt(channel, at)

    if (!block) {
      this.#hint(this.noRecordingTextValue)
      return
    }

    const start = Math.max(block.start, at - (at % 60))
    this.#select(channel, start, Math.min(block.end, start + this.defaultSecondsValue))
  }

  pickBlock({ params: { channel, start, end } }) {
    this.#select(String(channel), start, Math.min(end, start + this.defaultSecondsValue))
    this.formTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  edited() {
    const range = this.#range()
    this.actionTargets.forEach((button) => { button.disabled = !range || range.tooLong })
    this.#drawMark(range)

    if (range?.tooLong) {
      this.#hint(this.tooLongTextValue)
    } else if (range) {
      this.#hint(`${this.#clock(range.start)} → ${this.#clock(range.end)} · ${this.#duration(range.end - range.start)}`)
    } else {
      this.#hint(this.hintTextValue)
    }
  }

  play() {
    const range = this.#range()
    if (!range) return

    const player = this.application.getControllerForElementAndIdentifier(this.element, "jimi-stream")
    const section = document.getElementById("recording-player")
    section?.classList.remove("hidden")
    section?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    player?.playRecording({
      params: { channel: range.channel, from: this.#stamp(range.start), to: this.#stamp(range.end) }
    })
  }

  #select(channel, start, end) {
    this.channelTarget.value = channel
    this.fromTarget.value = this.#clock(start)
    this.toTarget.value = this.#clock(end)
    this.edited()
  }

  #range() {
    const start = this.#seconds(this.fromTarget.value)
    const end = this.#seconds(this.toTarget.value)
    if (start === null || end === null || end <= start) return null

    return { channel: this.channelTarget.value, start, end, tooLong: end - start > this.maxSecondsValue }
  }

  #blockAt(channel, at) {
    const block = this.blockTargets.find((element) =>
      element.dataset.channel === channel && Number(element.dataset.start) <= at && at < Number(element.dataset.end)
    )
    return block && { start: Number(block.dataset.start), end: Number(block.dataset.end) }
  }

  #drawMark(range) {
    this.markTargets.forEach((mark) => {
      const visible = range && mark.dataset.channel === range.channel
      mark.classList.toggle("hidden", !visible)
      if (!visible) return

      mark.style.left = `${(range.start / DAY_SECONDS) * 100}%`
      mark.style.width = `${Math.max(((range.end - range.start) / DAY_SECONDS) * 100, 0.4)}%`
    })
  }

  #hint(text) {
    if (this.hasHintTarget) this.hintTarget.textContent = text
  }

  #seconds(value) {
    const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || "")
    if (!match) return null

    return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] || 0)
  }

  #clock(seconds) {
    const clamped = Math.min(Math.max(seconds, 0), DAY_SECONDS - 1)
    return [Math.floor(clamped / 3600), Math.floor((clamped % 3600) / 60), clamped % 60]
      .map((part) => String(part).padStart(2, "0"))
      .join(":")
  }

  #stamp(seconds) {
    return `${this.dateValue} ${this.#clock(seconds)}`
  }

  #duration(seconds) {
    const minutes = Math.floor(seconds / 60)
    const rest = seconds % 60
    return minutes > 0 ? `${minutes} min${rest ? ` ${rest} s` : ""}` : `${rest} s`
  }
}
